import React from 'react';
import { Link } from 'react-router-dom';

export default function OpenAIKeyGuidePage(){
  return (
    <main className="article-page">
      <article className="article-shell">
        <header className="article-header container">
          <Link className="article-back" to="/blog/guides">← All guides</Link>
          <div className="article-meta"><span>Guide</span><time>August 2, 2026</time><span>4 min read</span></div>
          <h1>How to create an OpenAI API key for Promptera</h1>
          <p className="article-dek">Create a private OpenAI project key, connect it through Promptera’s Key Vault, and continue running models without using Promptera credits.</p>
        </header>

        <div className="container"><div className="article-content glass">
          <p className="update-note"><strong>Before you begin:</strong> OpenAI API billing is separate from a ChatGPT subscription. Having ChatGPT Plus, Pro, Business, or another ChatGPT plan does not automatically include API usage.</p>

          <h2>1. Sign in to the OpenAI API Platform</h2>
          <p>Open the <a href="https://platform.openai.com/" target="_blank" rel="noopener noreferrer">OpenAI API Platform</a> and sign in with the account that should own the API usage. If you belong to more than one organization, confirm that the correct organization is selected.</p>

          <h2>2. Create or choose a project</h2>
          <p>Open your organization settings and select the project you want to use with Promptera. A dedicated project makes its usage, limits, and keys easier to monitor separately from other applications.</p>

          <h2>3. Configure API billing and limits</h2>
          <p>Open the <a href="https://platform.openai.com/settings/organization/billing" target="_blank" rel="noopener noreferrer">API billing settings</a> and follow OpenAI’s current instructions to enable API usage. Review any prepaid-credit, automatic-recharge, budget, and usage-limit options before confirming them.</p>
          <p>Charges for calls made with this key belong to your OpenAI API account. Promptera does not charge Promptera credits when Personal key is selected.</p>

          <h2>4. Create a secret API key</h2>
          <ol>
            <li>Open the selected project’s <strong>API Keys</strong> page.</li>
            <li>Select <strong>Create new secret key</strong>.</li>
            <li>Give it a recognizable name such as <strong>Promptera</strong>.</li>
            <li>Choose permissions appropriate for your project. If you use restricted permissions, allow the Responses API operations Promptera needs.</li>
            <li>Create the key and copy it immediately. OpenAI may not show the complete secret again.</li>
          </ol>

          <h2>5. Save it in Promptera</h2>
          <ol>
            <li>Open Promptera and select <strong>Settings</strong>.</li>
            <li>Open <strong>Providers &amp; Key Vault</strong>.</li>
            <li>Paste the key into the OpenAI key field and save it.</li>
            <li>Wait for Promptera to confirm that the key is connected.</li>
            <li>Open <strong>Quick Settings</strong> and change the AI source to <strong>Personal key</strong>.</li>
          </ol>

          <h2>Keep the key safe</h2>
          <ul>
            <li>Never send the key through email, chat, screenshots, or shared documents.</li>
            <li>Do not commit it to a Git repository or expose it in browser-side code.</li>
            <li>Use a separate project key for Promptera when practical.</li>
            <li>Review usage and budgets in the OpenAI Platform regularly.</li>
            <li>Revoke and replace the key immediately if you think it was exposed.</li>
          </ul>

          <h2>Troubleshooting</h2>
          <p>If Promptera cannot verify the key, check that it was copied completely, belongs to the intended project, has the required permissions, and that the API account has usable billing or credits. Creating a new key is safer than repeatedly sharing or editing an exposed one.</p>

          <p className="update-note">Official references: <a href="https://platform.openai.com/docs/quickstart/make-your-first-api-request" target="_blank" rel="noopener noreferrer">OpenAI API quickstart</a>, <a href="https://help.openai.com/en/articles/9186755-managing-projects-in-the-api-platform" target="_blank" rel="noopener noreferrer">project and API-key management</a>, and <a href="https://help.openai.com/en/articles/5008148-can-i-share-my-api-key-with-my-teammatecoworker" target="_blank" rel="noopener noreferrer">API-key safety</a>.</p>
        </div></div>
      </article>
    </main>
  );
}
